export interface IProductBarcodeDetails {
  barcode_id: string;
  code: string;
  custom: boolean;
  type: string | null;
  product_id: string;
  created_on: string;
  updated_on: string;
  deleted_on: string | null;
}

export interface IProductImageDetails {
  image_id: string;
  file_name: string;
  cdn: string;
  product_id: string;
  source_id: string | null;
  created_on: string;
  updated_on: string;
  deleted_on: string | null;
}

export interface IProductDetails {
  product_id: string;
  product_code: string;
  name: string;
  sanitized_name: string;
  description: string;
  nutritional_info: string | null;
  min_quantity: number;
  max_quantity: number;
  created_on: string;
  updated_on: string;
  deleted_on: string | null;
  barcodes: IProductBarcodeDetails[];
  images: IProductImageDetails[];
}

export interface IMetaData {
  data: {
    barcodes: string[];
    legacyId: string;
    subAppId: string;
    productName: string;
    productImage: string;
    lastChangeDtm: string;
    transactionId: string;
    shortProductName: string;
  };
  error: {};
  response: {
    smartCoolerProductId: string;
  };
  updated_by: string;
}

export interface ITransactionDetails {
  scp_transaction_id: string;
  action: string;
  processed: boolean;
  status_code: number;
  meta: IMetaData;
  scp_id: string;
  sub_app_id: string;
  created_on: string;
  deleted_on: string | null;
}

export interface ISmartCoolerSingleProductResponse {
  smartCoolerProductId: string;
  lintedSmartCoolerProductId: string;
  legacyId: string;
  productId: string;
  subAppId: string;
  barcodes: string[];
  productName: string;
  shortProductName: string;
  productImage: string;
  lastChangeDtm: string;
  createdOn: string;
  updatedOn: string;
  product: IProductDetails;
  transactions: ITransactionDetails[];
}

export const CONFIRMATION_MODALS = {
  ACTIVATE: {
    DESCRIPTION: 'Are you sure you want to activate product: ',
    TITLE: 'Activate Smart Cooler Product',
    PLACEHOLDER: 'please type `activate`',
    REGISTER_STRING: 'activateConfirm',
    CONFIRMATION_TEXT: 'activate',
    CONFIRMATION_ERROR: "Please type 'activate' to confirm.",
    MODAL_CONFIRMATION_TEXT: 'Activate',
    REQUIRED: 'This field is required.',
    CANCEL: 'Cancel',
  },
  DELETE: {
    DESCRIPTION: 'Are you sure you want to delete product: ',
    TITLE: 'Delete Smart Cooler Product',
    PLACEHOLDER: 'please type `delete`',
    REGISTER_STRING: 'deleteConfirm',
    CONFIRMATION_TEXT: 'delete',
    CONFIRMATION_ERROR: "Please type 'delete' to confirm.",
    MODAL_CONFIRMATION_TEXT: 'Delete',
    REQUIRED: 'This field is required.',
    CANCEL: 'Cancel',
  },
};

export const SMART_COOLER_PRODUCTS_PAGE_RESPONSE_MESSAGES = {
  TOAST_MESSAGES: {
    DELETION: {
      SUCCESS: 'Smart Cooler Product deleted successfully',
      FAILURE: 'Failed to delete Smart Cooler Product',
    },
    ACTIVATION: {
      SUCCESS: 'Smart Cooler Product activated successfully',
      FAILURE: 'Failed to activate Smart Cooler Product',
      BACKEND_RESPONSE: 'Successfully restored Smart Cooler Product.',
    },
  },
};

export enum ESmartCoolerProductTransactionAction {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  RESTORE = 'RESTORE',
}

// ================= Utility Function =================
export const getUserDisplayName = (userData: any): string => {
  if (!userData) return '—';
  const { name, given_name, family_name, nickname, email } = userData;
  if (name) return name;
  if (given_name && family_name) return `${given_name} ${family_name}`;
  if (nickname) return nickname;
  if (email) return email;
  return '—';
};

/**
 * Generates the descriptive message for the transaction status.
 * @param action The transaction action.
 * @param updatedBy The user who performed the action.
 * @param statusCode The transaction status code.
 * @returns The descriptive message string.
 */
export const transactionStatusDescription = (
  action: ESmartCoolerProductTransactionAction | string,
  updatedBy: string,
  statusCode: number,
): string => {
  if (statusCode === 0) {
    switch (action) {
      case ESmartCoolerProductTransactionAction.CREATE:
        return `Product created successfully by `;
      case ESmartCoolerProductTransactionAction.UPDATE:
        return `Product updated successfully by `;
      case ESmartCoolerProductTransactionAction.DELETE:
        return `Product deleted successfully by `;
      case ESmartCoolerProductTransactionAction.RESTORE:
        return `Product restored successfully by `;
      default:
        return `Unknown successful action completed by `;
    }
  } else {
    return `${action}-Failed with Status: ${statusCode}. Action performed by `;
  }
};

export const CREATE_SMART_COOLER_PRODUCTS_PROPS = {
  PRODUCT_CREATION: {
    SUCCESS_MESSAGE: 'Smart Cooler Product created successfully!',
    FAILURE_MESSAGE: 'Failed to create Smart Cooler Product.',
  },
  MODAL: {
    CREATE_TITLE: 'Create Smart Cooler Product',
    CREATE_SUBTITLE: 'Enter the details to create the product.',
    EDIT_TITLE: 'Edit Smart Cooler Product',
    EDIT_SUBTITLE: 'Update the details of the product.',
  },
  CREATE_SCP_FORM_PROPS: {
    SUB_APPLICATION: {
      LABEL: 'Sub Application',
      IS_REQUIRED: 'Sub-application is a required field.',
      INFO: 'Select the sub-application from the dropdown',
      PLACEHOLDER: 'Select Sub-Application',
      REACT_HOOK_FORM_KEY_NAME: 'subAppId',
    },
    SHORT_PRODUCT_NAME: {
      LABEL: 'Short Product Name',
      INFO: 'A brief, recognizable name for the product (3-64 characters).',
      PLACEHOLDER: 'Enter short product name',
      VALIDATION: {
        REQUIRED: 'Short product name is required.',
        MIN_LENGTH: 'Min length is 3 characters.',
        MAX_LENGTH: 'Max length is 64 characters.',
      },
      REACT_HOOK_FORM_KEY_NAME: 'shortProductName',
    },
    FULL_PRODUCT_NAME: {
      LABEL: 'Product Name',
      INFO: 'The full, official product name (Optional, max 128 characters).',
      PLACEHOLDER: 'Enter product name',
      VALIDATION: {
        MIN_LENGTH: 'Min length is 3 characters.',
        MAX_LENGTH: 'Max length is 128 characters.',
      },
      REACT_HOOK_FORM_KEY_NAME: 'productName',
    },
    SMART_PRODUCT_ID: {
      LABEL: 'Smart Product ID',
      INFO: 'The unique identifier for the product, typically a UUID (32 or 36 characters).',
      PLACEHOLDER: 'Enter Smart Product ID (UUID)',
      TOGGLE_LABELS: {
        HYPHENED_36_CHARS: 'HYPHENED (36 Chars)',
        NON_HYPHENED_32_CHARS: 'NON-HYPHENED (32 Chars)',
      },
      VALIDATION: {
        REQUIRED: 'Smart Product ID is required.',
        LENGTH_ERROR: 'Length should be between 32 and 36 characters.',
      },
      REACT_HOOK_FORM_KEY_NAME: 'smartCoolerProductId',
    },
    LEGACY_ID: {
      LABEL: 'Legacy ID',
      INFO: 'Optional field, max 32 characters.',
      PLACEHOLDER: 'Enter legacy ID (max 32 chars)',
      VALIDATION: {
        MAX_LENGTH: 'Max length is 32 characters.',
        INVALID_FORMAT:
          'Only numeric values are not allowed. Must contain at least one alphabet.',
      },
      REACT_HOOK_FORM_KEY_NAME: 'legacyId',
    },
    BARCODES: {
      LABEL: 'Barcodes',
      INFO: 'Only AlphaNumeric Values are allowed separated by commas (e.g., 123,456).',
      PLACEHOLDER: 'Enter barcodes separated by comma (e.g., 121,131)',
      REACT_HOOK_FORM_KEY_NAME: 'barcodes',
    },

    PRODUCT_IMAGE: {
      LABEL: 'Product Image URL',
      INFO: 'A direct URL pointing to the product image (Optional, must be a valid URL).',
      PLACEHOLDER: 'Enter product image URL',
      VALIDATION: {
        PATTERN: /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i,
        INVALID_URL: 'Please enter a valid URL.',
        MAX_LENGTH_MSG: 'Max length is 1000 characters.',
      },
      REACT_HOOK_FORM_KEY_NAME: 'productImage',
    },
  },
} as const;

export const SMART_COOLER_COLUMNS = {
  SCP_ID: 'SCP ID',
  LEGACY_ID: 'Legacy ID',
  SUBAPP: 'SubApp',
  PRODUCT_NAME: 'Product Name',
  IMAGE: 'Image',
  STATUS: 'Active',
  LAST_CHANGED: 'Last Changed',
  CREATED_ON: 'Created On',
  UPDATED: 'Updated On',
  ACTION: 'Action',
};

export const SMART_COOLER_PAGE_PROPS = {
  SHOW_DELETED_LABEL: 'Show Deleted',
  DELETE_MODAL_SUBTITLE: 'This action cannot be undone.',
  DELETE_INPUT_LABEL: 'Type to confirm deletion',
  DELETE_HINT: 'to enable the confirmation button.',
  ACTIVATE_MODAL_SUBTITLE: 'This will restore the deleted product.',
  ACTIVATE_INPUT_LABEL: 'Type to confirm activation',
  ACTIVATE_HINT: 'to enable the confirmation button.',
};
